<template>
  <div data-cy="webhook-endpoints-tab">
    <div class="mb-4 flex flex-wrap items-center justify-between gap-2">
      <p class="m-0 max-w-3xl text-sm text-gray-600">
        An endpoint is a URL on your server. Sera sends it a signed POST each
        time an event it's subscribed to happens.
      </p>
      <button
        v-if="!readOnly"
        type="button"
        class="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-60"
        :disabled="loading || atLimit"
        :title="atLimit ? limitCopy : undefined"
        data-cy="webhook-add-endpoint"
        @click="openForm(null)"
      >
        Add endpoint
      </button>
    </div>

    <p
      v-if="atLimit && !readOnly"
      class="mb-3 text-sm text-gray-600"
      data-cy="webhook-limit-reached"
    >
      {{ limitCopy }}
    </p>

    <div v-if="loading" class="flex justify-center py-10">
      <Spinner />
    </div>

    <Alert
      v-else-if="loadFailed"
      type="error"
      data-cy="webhook-endpoints-error"
    >
      Your webhook endpoints couldn't be loaded.
      <button type="button" class="link ml-1" @click="load">Try again</button>
    </Alert>

    <p
      v-else-if="!endpoints.length"
      class="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600"
      data-cy="webhook-endpoints-empty"
    >
      No endpoints yet. Add one to start receiving events.
    </p>

    <div
      v-else
      class="overflow-hidden rounded-lg border border-gray-200 bg-white"
      data-cy="webhook-endpoints-list"
    >
      <div
        class="hidden border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase text-gray-500 md:grid md:grid-cols-[2.5fr_1fr_1fr_1fr_1.2fr] md:gap-4"
      >
        <span>Endpoint</span>
        <span>Events</span>
        <span>Status</span>
        <span>Success (24h)</span>
        <span>Last delivery</span>
      </div>
      <button
        v-for="endpoint in endpoints"
        :key="endpoint.id"
        type="button"
        class="grid w-full grid-cols-1 gap-1 border-b border-gray-100 px-4 py-3 text-left text-sm last:border-b-0 hover:bg-gray-50 md:grid-cols-[2.5fr_1fr_1fr_1fr_1.2fr] md:items-center md:gap-4"
        :data-cy="`webhook-endpoint:${endpoint.id}`"
        @click="openDrawer(endpoint.id)"
      >
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <span class="truncate font-medium text-gray-900">{{
              endpointLabel(endpoint)
            }}</span>
            <span
              class="whitespace-nowrap rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-600"
              data-cy="webhook-endpoint-kind"
              >{{ endpoint.kind === "zapier" ? "Zapier" : "Webhook" }}</span
            >
          </div>
          <div class="truncate text-xs text-gray-500">
            {{ endpoint.destination.display }} ·
            <code :title="FINGERPRINT_TITLE">{{
              endpoint.destination.fingerprint
            }}</code>
          </div>
        </div>
        <div class="text-gray-700" data-cy="webhook-endpoint-events">
          {{ endpoint.event_types.length }}
          {{ endpoint.event_types.length === 1 ? "event" : "events" }}
        </div>
        <div>
          <span
            class="whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold"
            :class="healthStyle(endpoint.health).pill"
            data-cy="webhook-endpoint-health"
            >{{ healthStyle(endpoint.health).label }}</span
          >
          <span
            v-if="endpoint.paused_by_integration"
            class="mt-1 block text-xs text-gray-500"
            data-cy="webhook-endpoint-integration-off"
            >Webhooks are off</span
          >
        </div>
        <div class="text-gray-700" data-cy="webhook-endpoint-success-rate">
          {{ formatSuccessRate(endpoint.stats_24h.success_rate) }}
        </div>
        <div class="text-gray-700" data-cy="webhook-endpoint-last-delivery">
          <span
            v-if="endpoint.last_delivery_at"
            :title="formatDateTimeWithTimezone(endpoint.last_delivery_at)"
            >{{ fromNow(endpoint.last_delivery_at, true) }}</span
          >
          <span v-else class="text-gray-500">None yet</span>
        </div>
      </button>
    </div>

    <WebhookEndpointDrawer
      v-if="drawerId"
      :key="drawerId"
      :endpoint-id="drawerId"
      :event-types="eventTypes"
      :read-only="readOnly"
      @close="drawerId = null"
      @changed="replaceEndpoint"
      @archived="removeEndpoint"
      @edit="openForm"
    />

    <WebhookEndpointForm
      v-if="formOpen"
      :endpoint="editing"
      :event-types="eventTypes"
      @close="closeForm"
      @saved="onSaved"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { formatDateTimeWithTimezone } from "@/lib/helpers";

import Alert from "@/components/Seranova/Alert/Alert.vue";
import Spinner from "@/components/Seranova/Spinner/Spinner.vue";
import { fromNow } from "@/mixins";
import type {
  WebhookEndpoint,
  WebhookEndpointsResponse,
  WebhookEventType
} from "@/vuex/modules/integrationWebhooks";
import WebhookEndpointDrawer from "./WebhookEndpointDrawer.vue";
import WebhookEndpointForm from "./WebhookEndpointForm.vue";
import { endpointLabel, formatSuccessRate, healthStyle } from "./webhooksHub";

const props = withDefaults(
  defineProps<{ eventTypes: WebhookEventType[]; readOnly?: boolean }>(),
  { readOnly: false }
);

const FINGERPRINT_TITLE =
  "Tells apart endpoints on the same host. Lists never show the full URL.";

const store = useStore();

const endpoints = ref<WebhookEndpoint[]>([]);
const limit = ref(20);
const loading = ref(false);
const loadFailed = ref(false);
const drawerId = ref<string | null>(null);
const formOpen = ref(false);
const editing = ref<WebhookEndpoint | null>(null);

const atLimit = computed(() => endpoints.value.length >= limit.value);
const limitCopy = computed(
  () =>
    `You can have at most ${limit.value} endpoints. Archive one to add another.`
);

async function load() {
  loading.value = true;
  loadFailed.value = false;
  try {
    const response: WebhookEndpointsResponse = await store.dispatch(
      "fetchWebhookEndpoints"
    );
    endpoints.value = response.data;
    limit.value = response.meta.limit ?? 20;
  } catch {
    loadFailed.value = true;
  } finally {
    loading.value = false;
  }
}

function openDrawer(id: string) {
  formOpen.value = false;
  drawerId.value = id;
}

function openForm(endpoint: WebhookEndpoint | null) {
  if (props.readOnly) return;
  editing.value = endpoint;
  drawerId.value = null;
  formOpen.value = true;
}

function closeForm() {
  const reopen = editing.value?.id ?? null;
  formOpen.value = false;
  editing.value = null;
  if (reopen) drawerId.value = reopen;
}

// Lists never hold the full URL, so it's dropped from what the form returns.
function withoutUrl(endpoint: WebhookEndpoint): WebhookEndpoint {
  const row = { ...endpoint };
  delete row.url;
  return row;
}

function replaceEndpoint(endpoint: WebhookEndpoint) {
  const row = withoutUrl(endpoint);
  const index = endpoints.value.findIndex((e) => e.id === row.id);
  if (index === -1) endpoints.value.push(row);
  else endpoints.value.splice(index, 1, row);
}

function removeEndpoint(id: string) {
  endpoints.value = endpoints.value.filter((e) => e.id !== id);
  drawerId.value = null;
}

// After a save the drawer opens on the endpoint, where its secret is.
function onSaved(endpoint: WebhookEndpoint) {
  replaceEndpoint(endpoint);
  formOpen.value = false;
  editing.value = null;
  drawerId.value = endpoint.id;
}

onMounted(load);
</script>
